import { useCallback, useEffect, useRef, useState } from 'react';
import authedFetch from '../../../lib/authedFetch';

const KEY = 'mirror_frontline_tour_narrate';
const VOICE_KEY = 'mirror_frontline_tour_voice';
const API = process.env.REACT_APP_BACKEND_URL;
const synth = typeof window !== 'undefined' ? window.speechSynthesis : null;
export const STUDIO_VOICES = [['onyx', 'Onyx · deep, authoritative'], ['sage', 'Sage · wise, measured'], ['nova', 'Nova · energetic'], ['shimmer', 'Shimmer · bright'], ['fable', 'Fable · storytelling'], ['browser', 'Device voice (offline)']];

import { toSpeech } from './tourScript';
export { toSpeech };

const pickVoice = () => {
  const voices = synth.getVoices();
  const en = voices.filter((v) => /^en(-|_)/i.test(v.lang));
  const prefer = ['Google UK English Female', 'Google US English', 'Samantha', 'Karen', 'Daniel', 'Microsoft Aria', 'Microsoft Jenny'];
  return en.find((v) => prefer.some((p) => v.name.includes(p))) || en.find((v) => v.default) || en[0] || voices[0] || null;
};
const read = (k, d) => { try { return localStorage.getItem(k) ?? d; } catch { return d; } };
const write = (k, v) => { try { localStorage.setItem(k, v); } catch { /* noop */ } };

// Narrator for the guided tour. Studio voice = OpenAI TTS clips pre-generated server-side (cached) and played from blobs;
// falls back to the device's speech synthesis when offline / unavailable.
export function useNarrator(scriptSteps) {
  const [on, setOn] = useState(() => read(KEY, '0') === '1');
  const [voice, setVoiceState] = useState(() => read(VOICE_KEY, 'onyx'));
  const [speaking, setSpeaking] = useState(false);
  const [current, setCurrent] = useState(null); // { text, startedAt, durationMs } while a line is being spoken (drives captions)
  const [deviceVoice, setDeviceVoice] = useState('');
  const [studio, setStudio] = useState({ status: 'idle', ready: 0, total: 0, error: '' }); // idle | preparing | ready | failed
  const studioRef = useRef('idle'); studioRef.current = studio.status;
  const clips = useRef({}); // "voice|text" -> object URL
  const pending = useRef(null); // { key, text, onEnd } waiting for its clip while preparing
  const audio = useRef(null);
  const utter = useRef(null);
  const browserOk = !!synth && typeof window.SpeechSynthesisUtterance === 'function';
  const studioMode = voice !== 'browser';

  useEffect(() => { if (!browserOk) return undefined; const load = () => { const v = pickVoice(); setDeviceVoice(v ? v.name : ''); }; load(); synth.addEventListener?.('voiceschanged', load); return () => synth.removeEventListener?.('voiceschanged', load); }, [browserOk]);

  const stop = useCallback(() => {
    if (audio.current) { audio.current.onended = null; audio.current.pause(); audio.current = null; }
    if (browserOk) synth.cancel();
    utter.current = null; pending.current = null; setSpeaking(false); setCurrent(null);
  }, [browserOk]);

  const sayBrowser = useCallback((text, onEnd) => {
    if (!browserOk) { if (onEnd) onEnd(); return; }
    synth.cancel();
    const u = new window.SpeechSynthesisUtterance(toSpeech(text));
    const v = pickVoice(); if (v) u.voice = v;
    u.onstart = () => { setSpeaking(true); setCurrent({ text, startedAt: Date.now(), durationMs: Math.max(1500, toSpeech(text).length * 62) }); };
    u.onend = () => { if (utter.current === u) { utter.current = null; setSpeaking(false); setCurrent(null); if (onEnd) onEnd(); } };
    u.onerror = u.onend;
    utter.current = u; synth.speak(u);
  }, [browserOk]);

  const playClip = useCallback((url, text, onEnd) => {
    const a = new Audio(url);
    a.onplay = () => { setSpeaking(true); setCurrent({ text, startedAt: Date.now(), durationMs: Number.isFinite(a.duration) && a.duration > 0 ? a.duration * 1000 : Math.max(1500, toSpeech(text).length * 62) }); };
    a.onended = () => { if (audio.current === a) { audio.current = null; setSpeaking(false); setCurrent(null); if (onEnd) onEnd(); } };
    a.onerror = () => { if (audio.current === a) { audio.current = null; sayBrowser(text, onEnd); } };
    audio.current = a;
    a.play().catch(() => { if (audio.current === a) { audio.current = null; sayBrowser(text, onEnd); } });
  }, [sayBrowser]);

  const say = useCallback((text, onEnd) => {
    stop(); pending.current = null;
    const k = `${voice}|${toSpeech(text)}`;
    const url = studioMode ? clips.current[k] : null;
    if (url) { playClip(url, text, onEnd); return; }
    if (studioMode && studioRef.current === 'preparing') { pending.current = { key: k, text, onEnd }; return; } // clip arrives shortly
    sayBrowser(text, onEnd);
  }, [stop, sayBrowser, playClip, studioMode, voice]);

  // Pre-generate + download the whole script once per voice so playback is instant.
  useEffect(() => {
    if (!on || !studioMode || !scriptSteps) { setStudio((p) => (p.status === 'idle' ? p : { status: 'idle', ready: 0, total: 0, error: '' })); return undefined; }
    let dead = false;
    const run = async () => {
      studioRef.current = 'preparing'; setStudio({ status: 'preparing', ready: 0, total: scriptSteps.length, error: '' });
      try {
        const r = await authedFetch(`${API}/api/mirror/frontline/narration/prepare`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ voice, steps: scriptSteps.map((s) => ({ id: s.id, text: toSpeech(s.text) })) }) });
        if (!r.ok) throw new Error(`prepare ${r.status}`);
        const data = await r.json();
        let ready = 0;
        await Promise.all(scriptSteps.map(async (s) => {
          const key = data.clips[s.id]; if (!key) return;
          const c = await authedFetch(`${API}/api/mirror/frontline/narration/clip/${key}.mp3`);
          if (!c.ok) return;
          const url = URL.createObjectURL(await c.blob());
          if (dead) { URL.revokeObjectURL(url); return; }
          const k = `${voice}|${toSpeech(s.text)}`;
          clips.current[k] = url; ready += 1;
          setStudio((p) => ({ ...p, ready }));
          if (pending.current && pending.current.key === k) { const w = pending.current; pending.current = null; playClip(url, w.text, w.onEnd); }
        }));
        if (!dead) setStudio({ status: ready ? 'ready' : 'failed', ready, total: scriptSteps.length, error: ready ? '' : 'No clips could be generated' });
      } catch (e) {
        if (!dead) setStudio({ status: 'failed', ready: 0, total: scriptSteps.length, error: 'Studio voice unavailable — using the device voice' });
      }
      if (!dead && pending.current) { const w = pending.current; pending.current = null; sayBrowser(w.text, w.onEnd); }
    };
    run();
    return () => { dead = true; };
  }, [on, voice, studioMode, scriptSteps, playClip, sayBrowser]);

  const toggle = () => { const next = !on; setOn(next); write(KEY, next ? '1' : '0'); if (!next) stop(); };
  const setVoice = (v) => { stop(); setVoiceState(v); write(VOICE_KEY, v); };
  useEffect(() => stop, [stop]);

  const supported = browserOk || studioMode;
  const label = studioMode ? (studio.status === 'ready' ? `Studio · ${voice[0].toUpperCase()}${voice.slice(1)}` : studio.status === 'preparing' ? `Preparing studio voice ${studio.ready}/${studio.total}…` : studio.status === 'failed' ? (deviceVoice || 'Device voice') : 'Studio voice') : (deviceVoice || 'Device voice');
  return { supported, on, toggle, say, stop, speaking, current, voice, setVoice, studio, label, usingStudio: studioMode && studio.status === 'ready' };
}
